import React, { FunctionComponent } from "react";
import PropTypes from "prop-types";
import { ApexOptions } from "apexcharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { connect } from "react-redux";
import Pie from "../../Charts/Pie";
import colorPalette from "../colorPalette/colorPalette";
import {
  Backdrop,
  ModalWrapper,
  Title,
  TitleText,
  CloseButton,
  Body,
  Legend,
} from "../styles/legend-modal-styles";

interface ITotalJobsPerSectorModalProps {
  totalJobsPerSector: {
    sectors?: Array<string>;
    values?: Array<number>;
  };
  toggleModal: () => void;
}

interface ITotalJobsPerSectorOptionsProps extends ApexOptions {
  series: Array<number>;
  options: ApexOptions;
}

interface ILegendOptionsProps {
  series: Array<number>;
  options: ApexOptions;
}

interface IFormatterProps {
  seriesIndex: number;
  w: {
    config: {
      series: () => void;
    };
  };
}

export const TotalJobsPerSectorModal: FunctionComponent<
  ITotalJobsPerSectorModalProps
> = ({ totalJobsPerSector, toggleModal }) => {
  const totalJobsPerSectorOptions: ITotalJobsPerSectorOptionsProps = {
    series: totalJobsPerSector.values,
    options: {
      chart: {
        height: "auto",
        dropShadow: {
          enabled: true,
          top: 0,
          left: 0,
          blur: 4,
          opacity: 0.1,
        },
      },
      colors: colorPalette(totalJobsPerSector.sectors.length),
      labels: totalJobsPerSector.sectors,
      dataLabels: {
        enabled: false,
      },
      legend: {
        show: false,
      },
      plotOptions: {
        pie: {
          donut: {
            size: "65%",
            background: "transparent",
          },
        },
      },
      tooltip: {
        enabled: true,
        style: {
          fontSize: "14px",
          fontFamily: "Komet, sans-serif",
        },
        x: {
          formatter: (seriesName) => seriesName as unknown as string,
        },
        y: {
          formatter: (value) => `${value as unknown as string} jobs`,
        },
      },
    },
  };

  const legendOptions: ILegendOptionsProps = {
    series: totalJobsPerSector.values,
    options: {
      colors: colorPalette(totalJobsPerSector.sectors.length),
      labels: totalJobsPerSector.sectors,
      dataLabels: {
        enabled: false,
      },
      legend: {
        show: true,
        horizontalAlign: "left",
        formatter: (name: string, { seriesIndex, w }: IFormatterProps) =>
          `${name} - ${w.config.series[seriesIndex] as number}`,
        itemMargin: {
          horizontal: 4,
          vertical: 4,
        },
        position: "bottom",
        containerMargin: {
          top: 0,
        },
      },
      tooltip: {
        enabled: false,
      },
      chart: {
        width: "100%",
        height: 100,
      },
    },
  };

  return (
    <Backdrop className="animated fadeIn">
      <ModalWrapper className="animated delay-1 fadeInDown">
        <Title>
          <TitleText>Total Jobs Per Sector</TitleText>
          <CloseButton onClick={() => toggleModal()}>
            <FontAwesomeIcon icon={faTimes} />
          </CloseButton>
        </Title>
        <Body className="animated fadeIn delay-5">
          <Pie
            graphOptions={totalJobsPerSectorOptions}
            dataTestId="spend-per-sector-chart"
          />
          <Legend>
            <Pie graphOptions={legendOptions} />
          </Legend>
        </Body>
      </ModalWrapper>
    </Backdrop>
  );
};

TotalJobsPerSectorModal.propTypes = {
  toggleModal: PropTypes.func.isRequired,
  totalJobsPerSector: PropTypes.shape({
    sectors: PropTypes.arrayOf(PropTypes.string),
    values: PropTypes.arrayOf(PropTypes.number),
  }).isRequired,
};

interface IReduxState {
  employerAnalytics: {
    chartsData: {
      totalJobsPerSector: unknown;
    };
  };
}

const mapStateToProps = (state: IReduxState) => ({
  totalJobsPerSector: state.employerAnalytics.chartsData.totalJobsPerSector,
});

export default connect(mapStateToProps, {})(TotalJobsPerSectorModal);
